import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Terminal, X } from 'lucide-react';
import { Button, Spinner } from '@vnetwork/ui/primitives';
import { useInstanceRemoteTicket, useInstanceSummary } from '@/api/vmware/instances';
import { env } from '@/config/env';

/**
 * VMware WebMKS remote console — opened in a popup window from OverviewTab.
 *
 * Mirrors the @console `VMwareConsole` function exactly:
 *   - Acquires a ticket from `/instances/:id/acquire_ticket`
 *   - Fetches the instance summary for `provider.value` (vmId) + `provider.serverGuid`
 *   - Builds an iframe URL:
 *       `${TUNNEL}/ui/webconsole.html?vmId=...&vmName=...&serverGuid=...&locale=...&tunnel=...&session=<stripped>`
 *   - Renders an <iframe> filling the popup window.
 *
 * The tunnel base comes from APP_SSH_TUNNEL_URL (env.webConsoleTunnelUrl).
 * When the tunnel is not configured, a friendly error is shown instead.
 *
 * Response shape (VERIFIED live):
 *   acquire_ticket → { acquire: { serverInfo: { name, ... }, locale }, session }
 *   summary        → { name, provider: { value, serverGuid }, summaryItems, ... }
 */
export function InstanceConsole() {
  const { serviceId, instanceId } = useParams<{ serviceId: string; instanceId: string }>();

  const { data: ticket, isLoading: ticketLoading, isError: ticketError } = useInstanceRemoteTicket(serviceId, instanceId);
  const { data: summary, isLoading: summaryLoading } = useInstanceSummary(serviceId, instanceId);

  const isLoading = ticketLoading || summaryLoading;
  const tunnel = env.webConsoleTunnelUrl;

  /**
   * Build the webconsole URL matching @console's VMwareConsole:
   *   vmId           = summary.provider.value
   *   vmName         = summary.name
   *   numMksConnections = 0
   *   serverGuid     = summary.provider.serverGuid
   *   locale         = ticket.acquire.locale
   *   tunnel         = ticket.acquire.serverInfo.name
   *   session        = ticket.session (spaces removed)
   */
  const consoleUrl = useMemo(() => {
    if (!ticket || !summary || !tunnel) return null;

    const params = new URLSearchParams();
    if (summary.provider?.value) params.set('vmId', summary.provider.value);
    if (summary.name) params.set('vmName', summary.name);
    params.set('numMksConnections', '0');
    if (summary.provider?.serverGuid) params.set('serverGuid', summary.provider.serverGuid);
    if (ticket.acquire?.locale) params.set('locale', ticket.acquire.locale);
    if (ticket.acquire?.serverInfo?.name) params.set('tunnel', ticket.acquire.serverInfo.name);

    const session = ticket.session?.replace(/ /g, '') ?? '';
    return `${tunnel}/ui/webconsole.html?${params.toString()}&session=${session}`;
  }, [ticket, summary, tunnel]);

  const name = summary?.name ?? instanceId ?? 'Console';
  const noTunnel = !tunnel;
  const hasError = ticketError || (!isLoading && !ticket);

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--vn-bg-canvas)' }}>
      {/* Minimal chrome */}
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 'var(--vn-space-3)', padding: 'var(--vn-space-2) var(--vn-space-4)',
        borderBottom: '1px solid var(--vn-border-default)', background: 'var(--vn-bg-surface)',
        flexShrink: 0,
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
          <Terminal size={16} /> {name}
        </span>
        <Button size="sm" variant="ghost" startIcon={<X size={15} />} onClick={() => window.close()}>
          Close
        </Button>
      </header>

      {isLoading ? (
        <div style={centeredStyle}>
          <Spinner />
          <span>Acquiring console session…</span>
        </div>
      ) : noTunnel ? (
        <div style={centeredStyle}>
          <p style={{ margin: 0, textAlign: 'center' }}>
            Console tunnel is not configured (APP_SSH_TUNNEL_URL not set).
          </p>
          <Button variant="secondary" onClick={() => window.close()}>Close window</Button>
        </div>
      ) : hasError ? (
        <div style={centeredStyle}>
          <p style={{ margin: 0, textAlign: 'center' }}>
            Console is unavailable. The instance may be powered off or still starting.
          </p>
          <Button variant="secondary" onClick={() => window.close()}>Close window</Button>
        </div>
      ) : consoleUrl ? (
        /* Full-height iframe — fills the popup window, no extra chrome */
        <iframe
          src={consoleUrl}
          style={{ flex: 1, border: 'none', width: '100%' }}
          referrerPolicy="origin"
          title={`Console — ${name}`}
        />
      ) : (
        <div style={centeredStyle}>
          <Spinner />
          <span>Preparing console…</span>
        </div>
      )}
    </div>
  );
}

const centeredStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  gap: 'var(--vn-space-4)',
  color: 'var(--vn-fg-muted)',
  padding: 'var(--vn-space-6)',
};
